<template>
  <div class="product-list product-list--b2b" ref="productList">
    <div
      class="popover"
      :style="`top: ${tagPopOver.top}px; left: ${tagPopOver.left}px`"
      v-if="tagHover"
    >
      <v-tag
        v-for="tag in tagPopOver.tags"
        :key="tag.id"
        :color="tag.color.colors[0]"
        :backgroundColor="tag.color.colors[1]"
      >
        {{ tag.name }}
      </v-tag>
    </div>
    <slot name="filters"></slot>
    <div class="product-list__alert">
      <slot name="alert" />
    </div>
    <h3 v-if="previouslyOrderedProductsCopy.length" class="previously-ordered-title mt-0 mb-16">
      Previously Ordered
    </h3>
    <ez-table
      v-if="previouslyOrderedProductsCopy.length && isListView"
      class="products-table"
      :headers="tableHeaders"
      :columns="tableColumns"
      :data="previouslyOrderedProductsCopy"
      :columnProps="tableColumnProps"
      :loading="isLoading"
      @rowClick="handleRowClick"
      :hasMinHeight="false"
    >
      <template #cell-name="{ row }">
        <ez-entity-info imgWidth="2rem" imgHeight="2rem" :imgUrl="row.image">
          <div :class="['product-info', { 'responsive-info': responsiveInfo }]" :title="row.name">
            <span :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_NAME}-${row.id}`">
              {{ row.name }}
            </span>
            <span class="product-info-secondary">
              <span
                class="product-info-secondary--category"
                :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_CATEGORY}-${row.id}`"
              >
                {{ row.category | categoryWithParent }}
              </span>
              <span
                v-if="row.sku"
                class="product-info-secondary--sku"
                :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_SKU}-${row.id}`"
              >
                &#8226; {{ row.sku }}
              </span>
            </span>
          </div>
        </ez-entity-info>
      </template>
      <template #cell-expiresSoon="{ row, row: { inventoryTracked, expiresSoon } }">
        <font-awesome-icon
          v-if="inventoryTracked && expiresSoon"
          v-tooltip="{
            placement: 'top-start',
            content: `This product has a batch that's about to expire soon`,
            classes: ['tooltip--reset-margin u-text-center mb-8'],
          }"
          icon="calendar-alt"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_EXPIRES_SOON}-${row.id}`"
        />
      </template>
      <template #cell-parLevelReached="{ row, row: { inventoryTracked, parLevelReached } }">
        <font-awesome-icon
          v-if="inventoryTracked && parLevelReached"
          v-tooltip="{
            placement: 'top-start',
            content: 'This product has reached its par level',
            classes: ['tooltip--reset-margin u-text-center mb-8'],
          }"
          icon="exclamation-circle"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PAR_LEVEL_REACHED}-${row.id}`"
        />
      </template>
      <template #cell-warehouses="{ row: { id, warehouses, locations, inventoryTracked } }">
        <font-awesome-icon
          v-if="!inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
            classes: ['tooltip--reset-margin u-text-center mb-2'],
          }"
        />
        <v-warehouse-location
          v-else
          :data="warehouseId ? locations : warehouses"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_WAREHOUSE}-${id}`"
        />
      </template>
      <template #cell-tags="{ row }">
        <div
          class="tags"
          v-if="row.tags.length"
          @mouseenter="tagsShowHover(row.id, row.tags)"
          @mouseleave="tagsRemoveHover"
          :ref="`tag-${row.id}`"
        >
          <v-tag
            class="mr-8"
            :color="row.tags[0].color.colors[0]"
            :backgroundColor="row.tags[0].color.colors[1]"
            >{{ row.tags[0].name }}</v-tag
          >
          <span v-if="row.tags.length > 1">+{{ row.tags.length - 1 }}</span>
        </div>
        <span v-else>-</span>
      </template>
      <template #cell-parLevel="{ row: { id, parLevel, inventoryTracked } }">
        <font-awesome-icon
          v-if="!inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PAR_LEVEL}-${id}`">
          {{ parLevel || '-' }}
        </span>
      </template>
      <template #cell-stock="{ row }">
        <template v-if="warehouseId || (inventoryDate && !isInventoryDateToday)">
          <font-awesome-icon
            v-if="!row.inventoryTracked"
            flip="horizontal"
            :icon="['far', 'eye-slash']"
            v-tooltip="{
              placement: 'top-start',
              content: 'Not Tracking',
            }"
          />
          <font-awesome-icon
            v-else-if="!row.available"
            v-tooltip="{
              placement: 'top',
              content: 'Out of stock',
              classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
            }"
            icon="minus-circle"
            class="text-red"
          />
          <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_STOCK}-${row.id}`">
            {{ row.inventory }}
          </span>
        </template>
        <template v-else>
          <font-awesome-icon
            v-if="!row.inventoryTracked"
            flip="horizontal"
            :icon="['far', 'eye-slash']"
            v-tooltip="{
              placement: 'top-start',
              content: 'Not Tracking',
              classes: ['tooltip--reset-margin u-text-center mb-2'],
            }"
          />
          <font-awesome-icon
            v-else-if="!row.available"
            v-tooltip="{
              placement: 'top',
              content: 'Out of stock',
              classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
            }"
            icon="minus-circle"
            class="text-red"
          />
          <v-product-quantity-inventory
            v-else
            :product="row"
            :canBeInvalid="false"
            warehouseProperty="warehouseInventory"
            isDistributor
            showTotalAsLabel
            inventoryTracking
          />
        </template>
      </template>
      <template #cell-category="{ row, row: { category } }">
        <span
          class="category-badge"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_CATEGORY}-${row.id}`"
        >
          {{ category | categoryWithParent }}
        </span>
      </template>
      <template #cell-distributor="{ row, row: { distributor } }">
        <ez-entity-info
          v-if="distributor"
          imgWidth="2.125rem"
          imgHeight="2.125rem"
          :imgUrl="distributor.logo"
          imgBorderRadius="50%"
        >
          <div
            class="distributor-info"
            :title="distributor.name"
            :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_SUPPLIER}-${row.id}`"
          >
            {{ distributor.name }}
          </div>
        </ez-entity-info>
      </template>
      <template #cell-importType="{ row, row: { importType } }">
        <span :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_IMPORT_TYPE}-${row.id}`">
          {{ importType === 'trade_gecko' ? $t('quickBooks.nameShort') : importType | capitalize }}
        </span>
      </template>
      <template #cell-quantity="{ row }">
        <font-awesome-icon
          v-if="!row.inventoryTracked"
          class="fa-regular fa-flip-horizontal"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <font-awesome-icon
          v-else-if="!row.available"
          v-tooltip="{
            placement: 'top',
            content: 'Out of stock',
            classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
          }"
          icon="minus-circle"
          class="text-red"
        />
        <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_STOCK}-${row.id}`">{{
          row.inventory
        }}</span>
      </template>
      <template #cell-unit="{ row: { id, orderingUnit } }">
        <template v-if="orderingUnit">
          <v-data-with-info
            :info="
              orderingUnit.abbreviation
                ? `${orderingUnit.name} ${!!$t(`product.unitInfo.${orderingUnit.type}`) ? '-' : ''}
            ${$t(`product.unitInfo.${orderingUnit.type}`)}`
                : ''
            "
            :show-underline="
              !!$t(`product.unitInfo.${orderingUnit.type}`) || !!orderingUnit.abbreviation
            "
            :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_ORDERING_UNIT}-${id}`"
          >
            {{ orderingUnit.label }}
          </v-data-with-info>
        </template>
      </template>
      <template #cell-cogs="{ row: { id, cogs, inventoryTracked } }">
        <font-awesome-icon
          v-if="trackingInventory && !inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <v-price
          v-else
          :price="cogs || 0"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_COGS}-${id}`"
        />
      </template>
      <template #cell-price="{ row: { id, price, priceUnit, marketPrice } }">
        <v-price
          :price="price || 0"
          :unit="priceUnit && priceUnit.label"
          :is-market-price="marketPrice"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PRICE}-${id}`"
          :currency="b2bSupplier?.distributor?.currency || null"
        />
      </template>
      <template #cell-availability="{ row, row: { customerInventory } }">
        <font-awesome-icon
          v-if="customerInventory?.outOfStock"
          v-tooltip="{
            placement: 'top',
            content: 'Out of stock',
            classes: ['tooltip', 'tooltip--lift-up'],
          }"
          icon="minus-circle"
          class="text-red mx-4"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_OUT_OF_STOCK}-${row.id}`"
        />
        <font-awesome-icon
          v-if="customerInventory?.lowInventory"
          v-tooltip="{
            placement: 'top',
            content: 'Low inventory',
            classes: ['tooltip', 'tooltip--lift-up'],
          }"
          icon="exclamation-circle"
          class="text-carrot mx-4"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_LOW_INVENTORY}-${row.id}`"
        />
        {{ customerInventory?.inventoryLevel }}
      </template>
      <template #cell-addToCart="{ row }" v-if="hasQuickAdd && $permission.has('createOrder')">
        <div class="quantity">
          <ez-mask-input-simple
            name="quantity"
            formKey=""
            :minValue="row.minimumQuantity ?? 0"
            :maxValue="row.applyIncrement ? productQuantityMaxValue(row) : Number.MAX_SAFE_INTEGER"
            :value="row.quantity"
            :disabled="productQuantityDisabled(row)"
            @input="onQuantityChange($event, row.id)"
            :precision="isFractional(row.orderingUnit) ? 2 : 0"
            @invalid="row.applyIncrement ? onQuantityInvalid($event, row.id) : null"
            :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${row.id}`"
            :has-currency="false"
            :is-invalid="row.applyIncrement ? checkIfValid(row) : false"
          />
          <div class="quantity__icon">
            <div
              v-tooltip.bottom="{
                content: productCartTooltipContent(row),
                classes: ['tooltip-general'],
              }"
            >
              <ez-button
                :class="['add-to-cart-button', { 'add-to-cart-button--active': isInCart(row) }]"
                type="link"
                :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${row.id}`"
                @click="quickAddToCart(row)"
                :disabled="addToCartDisabled(row)"
              >
                <font-awesome-icon icon="cart-plus" />
              </ez-button>
            </div>
          </div>
        </div>
      </template>
    </ez-table>
    <ez-button
      v-if="isListView && previouslyOrderedProductsCount > previouslyOrderedProductsCopy.length"
      type="secondary"
      class="see-more-link mt-24"
      @click="() => $emit('filterPreviouslyOrdered')"
      >See More Products
      <font-awesome-icon icon="angle-right" style="font-size: 20px" class="ml-8" />
    </ez-button>
    <div
      class="products-table-mobile--b2b"
      v-if="previouslyOrderedProductsCopy.length && isListView"
    >
      <div
        v-for="product in previouslyOrderedProductsCopy"
        :key="product.id"
        class="product-item"
        :id="`product-item-${product.id}`"
        @click="handleRowClick(product)"
      >
        <div class="first-row">
          <ez-entity-info imgWidth="2.5rem" imgHeight="2.5rem" :imgUrl="product.image">
            <div class="product-info" :title="product.name">
              <span>
                {{ product.name }}
              </span>
              <span class="product-info-secondary">
                <span class="product-info-secondary--category category-badge">
                  {{ product.category | categoryWithParent }}
                </span>
              </span>
            </div>
          </ez-entity-info>
        </div>
        <hr />
        <div class="second-row">
          <v-price
            flex-row
            :price="product.price || 0"
            :is-market-price="product.marketPrice"
            :show-market-price-long="true"
            :show-market-price-info="false"
            :unit="product?.priceUnit?.labe || product?.orderingUnit?.label"
            :currency="product?.currency || b2bSupplier?.distributor?.currency || null"
          />
          <div
            class="quantity-input"
            @click.stop
            v-if="hasQuickAdd && $permission.has('createOrder')"
          >
            <div class="quantity">
              <ez-mask-input-simple
                name="quantity"
                formKey=""
                :minValue="product.minimumQuantity ?? 0"
                :maxValue="productQuantityMaxValue(product)"
                :value="product.quantity"
                :disabled="productQuantityDisabled(product)"
                @input="onQuantityChange($event, product.id)"
                :precision="isFractional(product.orderingUnit) ? 2 : 0"
                @invalid="product.applyIncrement ? onQuantityInvalid($event, product.id) : null"
                :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${product.id}`"
                :has-currency="false"
                :is-invalid="product.applyIncrement ? checkIfValid(product) : false"
              />
              <div class="quantity__icon">
                <div
                  v-tooltip.bottom="{
                    content: productCartTooltipContent(product),
                    classes: ['tooltip-general'],
                  }"
                >
                  <ez-button
                    :class="[
                      'add-to-cart-button',
                      { 'add-to-cart-button--active': isInCart(product) },
                    ]"
                    type="link"
                    :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${product.id}`"
                    @click="quickAddToCart(product)"
                    :disabled="addToCartDisabled(product)"
                  >
                    <font-awesome-icon icon="cart-plus" />
                  </ez-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ul v-if="!isListView && previouslyOrderedProductsCopy.length" class="products-grid">
      <li
        v-for="product in previouslyOrderedProductsCount <= 5
          ? previouslyOrderedProductsCopy
          : previouslyOrderedProductsCopy.slice(0, 4)"
        :key="product.id"
        class="product-card"
        @click="handleRowClick(product)"
      >
        <div class="product-card-image">
          <img :src="product.image" alt="Product Image" />
          <span class="unit-badge" v-if="product.priceUnit || product.orderingUnit">
            {{ (product.orderingUnit || product.priceUnit).label }}
          </span>
        </div>

        <div class="product-card-body">
          <p class="product-name">{{ product.name }}</p>
          <hr />
          <span class="product-category">
            {{ product.category | categoryWithParent }}
          </span>
          <v-price
            v-if="columns.includes('price')"
            class="mt-16"
            flex-row
            :price="product.price || 0"
            :is-market-price="product.marketPrice"
            :show-market-price-long="true"
            :show-market-price-info="false"
            :currency="product?.currency || b2bSupplier?.distributor?.currency || null"
          />
          <status-badge
            class="mt-12"
            :status="QUANTITY_STATUS_OUT_OF_STOCK"
            v-if="!product.available && columns.includes('price')"
          />
        </div>
        <div
          class="product-card-cart"
          v-if="
            hasQuickAdd &&
            $permission.has('createOrder') &&
            ((product.available && columns.includes('price')) || !columns.includes('price'))
          "
          @click.stop
        >
          <button
            class="increment"
            type="button"
            :disabled="
              product.quantity === (product.minimumQuantity || 0) ||
              productQuantityDisabled(product)
            "
            @click="onMinusClick(product)"
          >
            <font-awesome-icon size="xs" icon="minus" />
          </button>
          <ez-mask-input-simple
            name="quantity"
            formKey=""
            :minValue="product.minimumQuantity ?? 0"
            :maxValue="
              product.applyIncrement ? productQuantityMaxValue(product) : Number.MAX_SAFE_INTEGER
            "
            :value="product.quantity"
            :disabled="productQuantityDisabled(product)"
            @input="onQuantityChange($event, product.id)"
            :precision="isFractional(product.orderingUnit) ? 2 : 0"
            @invalid="product.applyIncrement ? onQuantityInvalid($event, product.id) : null"
            :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${product.id}`"
            :has-currency="false"
            :is-invalid="product.applyIncrement ? checkIfValid(product) : false"
          />
          <button
            class="increment"
            type="button"
            @click="onPlusClick(product)"
            :disabled="
              product.quantity + (product.orderingUnitIncrement || 1) >
                productQuantityMaxValue(product) || productQuantityDisabled(product)
            "
          >
            <font-awesome-icon size="xs" icon="plus" />
          </button>
        </div>
        <div
          class="quantity"
          v-if="
            hasQuickAdd &&
            $permission.has('createOrder') &&
            ((product.available && columns.includes('price')) || !columns.includes('price'))
          "
          @click.stop
        >
          <div class="quantity__icon">
            <div
              v-tooltip.bottom="{
                content: productCartTooltipContent(product),
                classes: ['tooltip-general'],
              }"
            >
              <ez-button
                :class="['add-to-cart-button', { 'add-to-cart-button--active': isInCart(product) }]"
                type="link"
                :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${product.id}`"
                @click="quickAddToCart(product)"
                :disabled="addToCartDisabled(product)"
              >
                <font-awesome-icon icon="cart-plus" />
              </ez-button>
            </div>
          </div>
        </div>
      </li>
      <li
        class="product-card product-card--see-all"
        @click="$emit('filterPreviouslyOrdered')"
        v-if="previouslyOrderedProductsCount > previouslyOrderedProductsCopy.length"
      >
        <div><font-awesome-icon icon="angle-right" style="font-size: 24px" /></div>
        <span class="mt-12">
          See {{ previouslyOrderedProductsCount - previouslyOrderedProductsCopy.length + 1 }} more
          {{
            'product'
              | pluralize(previouslyOrderedProductsCount - previouslyOrderedProductsCopy.length + 1)
          }}</span
        >
      </li>
    </ul>
    <h3 v-if="previouslyOrderedProductsCopy.length" class="mt-24 mb-24">All Products</h3>
    <ez-table
      v-if="productsCopy.length && isListView"
      class="products-table"
      :headers="tableHeaders"
      :columns="tableColumns"
      :data="productsCopy"
      :columnProps="tableColumnProps"
      :loading="isLoading"
      @rowClick="handleRowClick"
      :hasMinHeight="false"
    >
      <template #cell-name="{ row }">
        <ez-entity-info imgWidth="2rem" imgHeight="2rem" :imgUrl="row.image">
          <div :class="['product-info', { 'responsive-info': responsiveInfo }]" :title="row.name">
            <span :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_NAME}-${row.id}`">
              {{ row.name }}
            </span>
            <span class="product-info-secondary">
              <span
                class="product-info-secondary--category"
                :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_CATEGORY}-${row.id}`"
              >
                {{ row.category | categoryWithParent }}
              </span>
              <span
                v-if="row.sku"
                class="product-info-secondary--sku"
                :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_SKU}-${row.id}`"
              >
                &#8226; {{ row.sku }}
              </span>
            </span>
          </div>
        </ez-entity-info>
      </template>
      <template #cell-archived="{ row }">
        <font-awesome-icon
          v-if="row.deletedAt"
          icon="archive"
          v-tooltip="{
            content: 'Archived product',
            classes: ['tooltip-general'],
          }"
        ></font-awesome-icon>
      </template>
      <template #cell-expiresSoon="{ row, row: { inventoryTracked, expiresSoon } }">
        <font-awesome-icon
          v-if="inventoryTracked && expiresSoon && !row.deletedAt"
          v-tooltip="{
            placement: 'top-start',
            content: `This product has a batch that's about to expire soon`,
            classes: ['tooltip--reset-margin u-text-center mb-8'],
          }"
          icon="calendar-alt"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_EXPIRES_SOON}-${row.id}`"
        />
      </template>
      <template #cell-parLevelReached="{ row, row: { inventoryTracked, parLevelReached } }">
        <font-awesome-icon
          v-if="inventoryTracked && parLevelReached && !row.deletedAt"
          v-tooltip="{
            placement: 'top-start',
            content: 'This product has reached its par level',
            classes: ['tooltip--reset-margin u-text-center mb-8'],
          }"
          icon="exclamation-circle"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PAR_LEVEL_REACHED}-${row.id}`"
        />
      </template>
      <template #cell-warehouses="{ row: { id, warehouses, locations, inventoryTracked } }">
        <font-awesome-icon
          v-if="!inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
            classes: ['tooltip--reset-margin u-text-center mb-2'],
          }"
        />
        <v-warehouse-location
          v-else
          :data="warehouseId ? locations : warehouses"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_WAREHOUSE}-${id}`"
        />
      </template>
      <template #cell-tags="{ row }">
        <div
          class="tags"
          v-if="row.tags.length"
          @mouseenter="tagsShowHover(row.id, row.tags)"
          @mouseleave="tagsRemoveHover"
          :ref="`tag-${row.id}`"
        >
          <v-tag
            class="mr-8"
            :color="row.tags[0].color.colors[0]"
            :backgroundColor="row.tags[0].color.colors[1]"
            >{{ row.tags[0].name }}</v-tag
          >
          <span v-if="row.tags.length > 1">+{{ row.tags.length - 1 }}</span>
        </div>
        <span v-else>-</span>
      </template>
      <template #cell-parLevel="{ row: { id, parLevel, inventoryTracked } }">
        <font-awesome-icon
          v-if="!inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PAR_LEVEL}-${id}`">
          {{ parLevel || '-' }}
        </span>
      </template>
      <template #cell-stock="{ row }">
        <template v-if="warehouseId || (inventoryDate && !isInventoryDateToday)">
          <font-awesome-icon
            v-if="!row.inventoryTracked"
            flip="horizontal"
            :icon="['far', 'eye-slash']"
            v-tooltip="{
              placement: 'top-start',
              content: 'Not Tracking',
            }"
          />
          <font-awesome-icon
            v-else-if="!row.available"
            v-tooltip="{
              placement: 'top',
              content: 'Out of stock',
              classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
            }"
            icon="minus-circle"
            class="text-red"
          />
          <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_STOCK}-${row.id}`">
            {{ row.inventory }}
          </span>
        </template>
        <template v-else>
          <font-awesome-icon
            v-if="!row.inventoryTracked"
            flip="horizontal"
            :icon="['far', 'eye-slash']"
            v-tooltip="{
              placement: 'top-start',
              content: 'Not Tracking',
              classes: ['tooltip--reset-margin u-text-center mb-2'],
            }"
          />
          <font-awesome-icon
            v-else-if="!row.available"
            v-tooltip="{
              placement: 'top',
              content: 'Out of stock',
              classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
            }"
            icon="minus-circle"
            class="text-red"
          />
          <v-product-quantity-inventory
            v-else
            :product="row"
            :canBeInvalid="false"
            warehouseProperty="warehouseInventory"
            isDistributor
            showTotalAsLabel
            inventoryTracking
          />
        </template>
      </template>
      <template #cell-category="{ row, row: { category } }">
        <span
          class="category-badge"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_CATEGORY}-${row.id}`"
        >
          {{ category | categoryWithParent }}
        </span>
      </template>
      <template #cell-distributor="{ row, row: { distributor } }">
        <ez-entity-info
          v-if="distributor"
          imgWidth="2.125rem"
          imgHeight="2.125rem"
          :imgUrl="distributor.logo"
          imgBorderRadius="50%"
        >
          <div
            class="distributor-info"
            :title="distributor.name"
            :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_SUPPLIER}-${row.id}`"
          >
            {{ distributor.name }}
          </div>
        </ez-entity-info>
      </template>
      <template #cell-importType="{ row, row: { importType } }">
        <span :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_IMPORT_TYPE}-${row.id}`">
          {{ importType === 'trade_gecko' ? $t('quickBooks.nameShort') : importType | capitalize }}
        </span>
      </template>
      <template #cell-quantity="{ row }">
        <font-awesome-icon
          v-if="!row.inventoryTracked"
          class="fa-regular fa-flip-horizontal"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <font-awesome-icon
          v-else-if="!row.available"
          v-tooltip="{
            placement: 'top',
            content: 'Out of stock',
            classes: ['tooltip', 'tooltip--lift-up', 'tooltip--reset-margin'],
          }"
          icon="minus-circle"
          class="text-red"
        />
        <span v-else :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_STOCK}-${row.id}`">{{
          row.inventory
        }}</span>
      </template>
      <template #cell-unit="{ row: { id, orderingUnit } }">
        <template v-if="orderingUnit">
          <v-data-with-info
            :info="
              orderingUnit.abbreviation
                ? `${orderingUnit.name} ${!!$t(`product.unitInfo.${orderingUnit.type}`) ? '-' : ''}
            ${$t(`product.unitInfo.${orderingUnit.type}`)}`
                : ''
            "
            :show-underline="
              !!$t(`product.unitInfo.${orderingUnit.type}`) || !!orderingUnit.abbreviation
            "
            :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_ORDERING_UNIT}-${id}`"
          >
            {{ orderingUnit.label }}
          </v-data-with-info>
        </template>
      </template>
      <template #cell-cogs="{ row: { id, cogs, inventoryTracked } }">
        <font-awesome-icon
          v-if="trackingInventory && !inventoryTracked"
          flip="horizontal"
          :icon="['far', 'eye-slash']"
          v-tooltip="{
            placement: 'top-start',
            content: 'Not Tracking',
          }"
        />
        <v-price
          v-else
          :price="cogs || 0"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_COGS}-${id}`"
        />
      </template>
      <template #cell-price="{ row: { id, price, priceUnit, marketPrice } }">
        <v-price
          :price="price || 0"
          :unit="priceUnit && priceUnit.label"
          :is-market-price="marketPrice"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_PRICE}-${id}`"
          :currency="b2bSupplier?.distributor?.currency || null"
        />
      </template>
      <template #cell-availability="{ row, row: { customerInventory } }">
        <font-awesome-icon
          v-if="customerInventory?.outOfStock"
          v-tooltip="{
            placement: 'top',
            content: 'Out of stock',
            classes: ['tooltip', 'tooltip--lift-up'],
          }"
          icon="minus-circle"
          class="text-red mx-4"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_OUT_OF_STOCK}-${row.id}`"
        />
        <font-awesome-icon
          v-if="customerInventory?.lowInventory"
          v-tooltip="{
            placement: 'top',
            content: 'Low inventory',
            classes: ['tooltip', 'tooltip--lift-up'],
          }"
          icon="exclamation-circle"
          class="text-carrot mx-4"
          :data-cy="`${commonCy.PRODUCTS.TEXT__PRODUCT_LOW_INVENTORY}-${row.id}`"
        />
        {{ customerInventory?.inventoryLevel }}
      </template>
      <template #cell-addToCart="{ row }" v-if="hasQuickAdd && $permission.has('createOrder')">
        <div class="quantity">
          <ez-mask-input-simple
            name="quantity"
            formKey=""
            :minValue="row.minimumQuantity ?? 0"
            :maxValue="row.applyIncrement ? productQuantityMaxValue(row) : Number.MAX_SAFE_INTEGER"
            :value="row.quantity"
            :disabled="productQuantityDisabled(row)"
            @input="onQuantityChange($event, row.id)"
            :precision="isFractional(row.orderingUnit) ? 2 : 0"
            @invalid="row.applyIncrement ? onQuantityInvalid($event, row.id) : null"
            :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${row.id}`"
            :has-currency="false"
            :is-invalid="row.applyIncrement ? checkIfValid(row) : false"
          />
          <div class="quantity__icon">
            <div
              v-tooltip.bottom="{
                content: productCartTooltipContent(row),
                classes: ['tooltip-general'],
              }"
            >
              <ez-button
                :class="['add-to-cart-button', { 'add-to-cart-button--active': isInCart(row) }]"
                type="link"
                :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${row.id}`"
                @click="quickAddToCart(row)"
                :disabled="addToCartDisabled(row)"
              >
                <font-awesome-icon icon="cart-plus" />
              </ez-button>
            </div>
          </div>
        </div>
      </template>
    </ez-table>
    <div class="products-table-mobile--b2b" v-if="productsCopy.length && isListView">
      <div
        v-for="product in productsCopy"
        :key="product.id"
        class="product-item"
        :id="`product-item-${product.id}`"
        @click="handleRowClick(product)"
      >
        <div class="first-row">
          <ez-entity-info imgWidth="2.5rem" imgHeight="2.5rem" :imgUrl="product.image">
            <div class="product-info" :title="product.name">
              <span>
                {{ product.name }}
              </span>
              <span class="product-info-secondary">
                <span class="product-info-secondary--category category-badge">
                  {{ product.category | categoryWithParent }}
                </span>
              </span>
            </div>
          </ez-entity-info>
        </div>
        <hr />
        <div class="second-row">
          <v-price
            flex-row
            :price="product.price || 0"
            :is-market-price="product.marketPrice"
            :show-market-price-long="true"
            :show-market-price-info="false"
            :unit="product?.priceUnit?.labe || product?.orderingUnit?.label"
            :currency="product?.currency || b2bSupplier?.distributor?.currency || null"
          />
          <div
            class="quantity-input"
            @click.stop
            v-if="hasQuickAdd && $permission.has('createOrder')"
          >
            <div class="quantity">
              <div
                class="product-card-cart"
                v-if="
                  hasQuickAdd &&
                  $permission.has('createOrder') &&
                  ((product.available && columns.includes('price')) || !columns.includes('price'))
                "
                @click.stop
              >
                <button
                  class="increment"
                  type="button"
                  :disabled="
                    product.quantity === (product.minimumQuantity || 0) ||
                    productQuantityDisabled(product)
                  "
                  @click="onMinusClick(product)"
                >
                  <font-awesome-icon size="xs" icon="minus" />
                </button>
                <ez-mask-input-simple
                  name="quantity"
                  class="mobile-input"
                  formKey=""
                  :minValue="product.minimumQuantity ?? 0"
                  :maxValue="
                    product.applyIncrement
                      ? productQuantityMaxValue(product)
                      : Number.MAX_SAFE_INTEGER
                  "
                  :value="product.quantity"
                  :disabled="productQuantityDisabled(product)"
                  @input="onQuantityChange($event, product.id)"
                  :precision="isFractional(product.orderingUnit) ? 2 : 0"
                  @invalid="product.applyIncrement ? onQuantityInvalid($event, product.id) : null"
                  :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${product.id}`"
                  :has-currency="false"
                  :is-invalid="product.applyIncrement ? checkIfValid(product) : false"
                />
                <button
                  class="increment"
                  type="button"
                  @click="onPlusClick(product)"
                  :disabled="
                    product.quantity + (product.orderingUnitIncrement || 1) >
                      productQuantityMaxValue(product) || productQuantityDisabled(product)
                  "
                >
                  <font-awesome-icon size="xs" icon="plus" />
                </button>
              </div>
              <div class="quantity__icon">
                <div
                  v-tooltip.bottom="{
                    content: productCartTooltipContent(product),
                    classes: ['tooltip-general'],
                  }"
                >
                  <ez-button
                    :class="[
                      'add-to-cart-button',
                      { 'add-to-cart-button--active': isInCart(product) },
                    ]"
                    type="link"
                    :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${product.id}`"
                    @click="quickAddToCart(product)"
                    :disabled="addToCartDisabled(product)"
                  >
                    <font-awesome-icon icon="cart-plus" />
                  </ez-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ul v-if="!isListView" class="products-grid">
      <li
        v-for="product in productsCopy"
        :key="product.id"
        class="product-card"
        @click="handleRowClick(product)"
      >
        <div class="product-card-image">
          <img :src="product.image" alt="Product Image" />
          <span class="unit-badge" v-if="product.priceUnit || product.orderingUnit">
            {{ (product.orderingUnit || product.priceUnit).label }}
          </span>
        </div>

        <div class="product-card-body">
          <p class="product-name">{{ product.name }}</p>
          <hr />
          <span class="product-category">
            {{ product.category | categoryWithParent }}
          </span>
          <v-price
            v-if="columns.includes('price')"
            class="mt-16"
            flex-row
            :price="product.price || 0"
            :is-market-price="product.marketPrice"
            :show-market-price-long="true"
            :show-market-price-info="false"
            :currency="product?.currency || b2bSupplier?.distributor?.currency || null"
          />
          <status-badge
            class="mt-12"
            :status="QUANTITY_STATUS_OUT_OF_STOCK"
            v-if="!product.available && columns.includes('price')"
          />
        </div>
        <div
          class="product-card-cart"
          v-if="
            hasQuickAdd &&
            $permission.has('createOrder') &&
            ((product.available && columns.includes('price')) || !columns.includes('price'))
          "
          @click.stop
        >
          <button
            class="increment"
            type="button"
            :disabled="
              product.quantity === (product.minimumQuantity || 0) ||
              productQuantityDisabled(product)
            "
            @click="onMinusClick(product)"
          >
            <font-awesome-icon size="xs" icon="minus" />
          </button>
          <ez-mask-input-simple
            name="quantity"
            formKey=""
            :minValue="product.minimumQuantity ?? 0"
            :maxValue="
              product.applyIncrement ? productQuantityMaxValue(product) : Number.MAX_SAFE_INTEGER
            "
            :value="product.quantity"
            :disabled="productQuantityDisabled(product)"
            @input="onQuantityChange($event, product.id)"
            :precision="isFractional(product.orderingUnit) ? 2 : 0"
            @invalid="product.applyIncrement ? onQuantityInvalid($event, product.id) : null"
            :data-cy="`${commonCy.PRODUCTS.INPUT__PRODUCT_QUANTITY}-${product.id}`"
            :has-currency="false"
            :is-invalid="product.applyIncrement ? checkIfValid(product) : false"
          />
          <button
            class="increment"
            type="button"
            @click="onPlusClick(product)"
            :disabled="
              product.quantity + (product.orderingUnitIncrement || 1) >
                productQuantityMaxValue(product) || productQuantityDisabled(product)
            "
          >
            <font-awesome-icon size="xs" icon="plus" />
          </button>
        </div>
        <div
          class="quantity"
          v-if="
            hasQuickAdd &&
            $permission.has('createOrder') &&
            ((product.available && columns.includes('price')) || !columns.includes('price'))
          "
          @click.stop
        >
          <div class="quantity__icon">
            <div
              v-tooltip.bottom="{
                content: productCartTooltipContent(product),
                classes: ['tooltip-general'],
              }"
            >
              <ez-button
                :class="['add-to-cart-button', { 'add-to-cart-button--active': isInCart(product) }]"
                type="link"
                :data-cy="`${commonCy.PRODUCTS.BUTTON__PRODUCT_ADD_TO_CART}-${product.id}`"
                @click="quickAddToCart(product)"
                :disabled="addToCartDisabled(product)"
              >
                <font-awesome-icon icon="cart-plus" />
              </ez-button>
            </div>
          </div>
        </div>
      </li>
    </ul>
    <ez-loader v-if="!isListView" :show="isLoading">Loading...</ez-loader>
    <slot></slot>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { LOADING_KEY, QUANTITY_STATUS_OUT_OF_STOCK, UNIT_TYPE_FRACTIONAL } from '@/util/constants';
import { COMMON as commonCy } from '@weareneopix/qa-utils/dist/orderEz/common';

import EzMaskInputSimple from '@/components/ui/MaskInputSimple/EzMaskInputSimple.vue';
import EzEntityInfo from '@/components/ui/EntityInfo';
import EzButton from '@/components/ui/Button';
import EzTable from '@/components/ui/Table';
import EzLoader from '@/components/ui/Loader/EzLoader.vue';
import flash from '@/components/ui/FlashMessage';
import dayjs from 'dayjs';
import uuid from 'uuid/v4';

import VWarehouseLocation from '@/components/v3/elements/VWarehouseLocation';
import VDataWithInfo from '@/components/v3/elements/VDataWithInfo';
import VPrice from '@/components/v3/elements/VPrice';

import VTag from '@/components/v3/elements/VTag';
import VProductQuantityInventory from '@/components/v3/elements/VProductQuantityInventory';

import { isB2BPlatform, pluralize, formatPrice } from '@/util/utils';
import StatusBadge from '@/views/common/status-badge';
import Big from 'big.js';

export default {
  components: {
    VDataWithInfo,
    VPrice,
    EzEntityInfo,
    EzButton,
    EzTable,
    VWarehouseLocation,
    VTag,
    VProductQuantityInventory,
    EzMaskInputSimple,
    EzLoader,
    StatusBadge,
  },
  props: {
    headers: {
      type: Object,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    columnProps: {
      type: Object,
      default: null,
    },
    products: {
      type: Array,
      required: true,
    },
    previouslyOrderedProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
    previouslyOrderedProductsCount: {
      type: Number,
      required: false,
    },
    hasQuickAdd: {
      type: Boolean,
      required: false,
      default: false,
    },
    responsiveInfo: {
      type: Boolean,
      required: false,
      default: true,
    },
    warehouseId: {
      type: Number,
      required: false,
      default: null,
    },
    inventoryDate: {
      type: String,
      required: false,
      default: null,
    },
    b2bSupplier: {
      type: Object,
      required: false,
      default: () => {},
    },
    token: {
      required: false,
    },
    isListView: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      commonCy,
      productsCopy: [],
      previouslyOrderedProductsCopy: [],
      tagHover: false,
      tagPopOver: {
        tags: [],
        top: null,
        left: null,
      },
      isB2BPlatform,
      QUANTITY_STATUS_OUT_OF_STOCK,
      pluralize,
      formatPrice,
    };
  },
  computed: {
    ...mapState('entities/users', ['loggedUser']),
    ...mapState('cart', ['cart']),
    ...mapGetters('loading', ['getLoading']),
    isLoading() {
      return this.getLoading(LOADING_KEY.FETCH_PRODUCT) || this.getLoading(LOADING_KEY.ADD_TO_CART);
    },
    me() {
      return this.loggedUser.distributor;
    },
    isFreemium() {
      return this.$permission.isFree;
    },
    tableColumns() {
      return this.hasQuickAdd ? [...this.columns, 'addToCart'] : this.columns;
    },
    tableHeaders() {
      return this.hasQuickAdd
        ? {
            ...this.headers,
            addToCart: () => 'Quantity',
          }
        : this.headers;
    },
    tableColumnProps() {
      return this.hasQuickAdd
        ? {
            ...this.columnProps,
            addToCart: {
              class: 'add-to-cart-cell',
              on: {
                click: event => {
                  event.stopPropagation();
                },
              },
            },
            name: {
              class: 'product-cell',
            },
          }
        : this.columnProps;
    },
    trackingInventory() {
      return this.me.inventorySettings.tracking;
    },
    isInventoryDateToday() {
      const today = dayjs().endOf();
      if (dayjs(this.inventoryDate).isSame(today, 'day')) return true;
      return false;
    },
  },
  methods: {
    ...mapActions('cart', ['pushToCart']),
    handleRowClick(row) {
      this.$emit('rowClick', row);
    },
    onQuantityChange(quantity, id) {
      const pr = this.productsCopy.find(pro => pro.id === id);
      const prPrev = this.previouslyOrderedProductsCopy.find(pro => pro.id === id) || false;
      if (pr && pr.quantity !== quantity) {
        pr.quantity = quantity;
      }
      if (prPrev && prPrev?.quantity !== quantity) {
        prPrev.quantity = quantity;
      }
    },
    onQuantityInvalid(quantityInvalid, id) {
      const pr = this.productsCopy.find(pro => pro.id === id);
      const prPrev = this.previouslyOrderedProductsCopy.find(pro => pro.id === id);
      if (pr) pr.quantityInvalid = quantityInvalid;
      if (prPrev) prPrev.quantityInvalid = quantityInvalid;
      this.$forceUpdate();
    },
    checkIfValid(product) {
      const quantity = new Big(product?.quantity || 0);
      const remainder = quantity.mod(product?.orderingUnitIncrement || 1).valueOf();
      if (remainder === '0') return false;
      return true;
    },
    addToCartDisabled(row) {
      if (row.applyIncrement)
        return (
          row.quantityInvalid ||
          this.minimumQuantityUnavailable(row) ||
          row?.customerInventory?.outOfStock ||
          !(row.price >= 0) ||
          this.checkIfValid(row)
        );
      return false;
    },
    async quickAddToCart(product) {
      await this.$nextTick();

      const { minimumQuantity, quantity: selectedQuantity } = product;

      const quantity =
        selectedQuantity && selectedQuantity < minimumQuantity ? minimumQuantity : selectedQuantity;
      await this.pushToCart({
        product: { ...product, quantity, internalId: uuid() },
        ...(this.token ? { token: this.token } : {}),
      });

      const { href } = this.$router.resolve({
        name: 'venue-shopping-cart',
        ...(this.token
          ? {
              query: {
                token: this.token,
              },
            }
          : {}),
      });

      flash.success({
        title: 'Product successfully added to cart!',
        message: 'Go to cart to check all items you added and complete the order.',
        ctas: `<a href="${href}">Go to Cart</a>`,
      });
    },
    onMinusClick(product) {
      const updatedQuantity =
        product.quantity - (product.applyIncrement ? product.orderingUnitIncrement : 1) <
        product.minimumQuantity
          ? 0
          : product.quantity - (product.applyIncrement ? product.orderingUnitIncrement : 1);
      this.onQuantityChange(updatedQuantity, product.id);
    },
    onPlusClick(product) {
      const updatedQuantity =
        product.quantity + (product.applyIncrement ? product.orderingUnitIncrement : 1);
      this.onQuantityChange(updatedQuantity, product.id);
    },
    isFractional(orderingUnit) {
      return orderingUnit?.type === UNIT_TYPE_FRACTIONAL;
    },
    isInCart(row) {
      let isIn = false;
      Object.entries(this.cart).forEach(item => {
        if (item[1]) {
          Object.entries(item[1]).forEach(dist => {
            if (dist[1]?.product && dist[1]?.product?.id === row.id) isIn = true;
          });
        }
      });
      return isIn;
    },
    productQuantityMaxValue(row) {
      const { customerInventory: { orderMoreThanAvailable, inventoryLevel } = {} } = row;
      return !orderMoreThanAvailable && inventoryLevel !== null
        ? inventoryLevel
        : Number.MAX_SAFE_INTEGER;
    },
    productQuantityDisabled(row) {
      if (row.applyIncrement)
        return row?.customerInventory?.outOfStock || this.minimumQuantityUnavailable(row);
      return false;
    },
    productCartTooltipContent(row) {
      if (row?.customerInventory?.outOfStock) {
        return "Product is out of stock and can't be added to the cart.\n";
      }
      if (row.quantityInvalid) return 'Quantity is not valid';
      if (this.minimumQuantityUnavailable(row)) {
        return 'Product can’t be added to the cart because the available quantity is lower than the minimum order quantity.';
      }
      if (!row.marketPrice ? row.price >= 0 : true) return 'Add to Cart';
      return 'Invalid price';
    },
    minimumQuantityUnavailable(row) {
      const {
        minimumQuantity,
        customerInventory: { orderMoreThanAvailable, outOfStock, inventoryLevel } = {},
      } = row;
      return !orderMoreThanAvailable && !outOfStock && minimumQuantity > inventoryLevel;
    },
    tagsShowHover(id, tags) {
      if (!isB2BPlatform() || (isB2BPlatform() && tags.length > 1)) {
        this.tagHover = true;
        this.tagPopOver.tags = tags;
        const { top, left, bottom } = this.$refs[`tag-${id}`].getBoundingClientRect();
        const topParent = this.$refs.productList.getBoundingClientRect().top;
        const leftParent = this.$refs.productList.getBoundingClientRect().left;
        // Adding the height of the element
        this.tagPopOver.top = top - topParent + (bottom - top);
        this.tagPopOver.left = left - leftParent;
      }
    },
    tagsRemoveHover() {
      this.tagHover = false;
      this.tagPopOver = {
        tags: [],
        top: null,
        left: null,
      };
    },
  },
  watch: {
    products(newVal) {
      this.productsCopy = newVal;
    },
    previouslyOrderedProducts(newVal) {
      this.previouslyOrderedProductsCopy = newVal;
    },
  },
};
</script>

<style scoped lang="scss">
$input-button-bg-color: #f5f6fa;
.product-list {
  position: relative;

  :deep() .products-table-mobile--b2b {
    display: none;
    padding-bottom: 32px;
    width: 90vw;

    .product-item {
      padding: 12px 0;
      border-bottom: 1px solid #e1e5ed;
      hr {
        margin: 12px 0;
      }
      &:hover {
        cursor: pointer;
      }
    }

    .first-row {
      display: flex;
      align-items: center;
      line-height: 1.5;

      .entity-info {
        align-items: center;
        min-width: 0;
        flex-shrink: 1;
      }

      .product-quantity {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        color: $color-gray-6C;
        font-size: 14px;
        flex-shrink: 0;

        .product-info-secondary--category {
          font-size: 12px;
        }
      }
    }

    .second-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .quantity__icon {
        padding-left: 4px;
      }

      .quantity-input {
        display: flex;
        align-items: center;

        :deep() .mask-input-wrapper {
          max-width: 100px;
        }

        :deep() .mask-input-wrapper .mask-input .mask-input__input {
          border-radius: 0;
          font-size: 16px;
        }
      }
      .quantity .product-card-cart {
        display: flex;
        align-items: center;
        width: 120px;
        margin-right: 12px;

        :deep() .mask-input-wrapper {
          margin: 0 8px;
        }

        :deep() .mask-input-wrapper .mask-input .mask-input__input {
          border-radius: 0;
          font-size: 16px;
        }

        button.increment {
          display: flex;
          align-items: center;
          justify-content: center;
          @extend %button-reset;
          flex-shrink: 0;
          @include size(24px);
          background-color: $input-button-bg-color;
          border-radius: 50%;
          color: $color-gray-6C;

          &:hover {
            cursor: pointer;
            background-color: #e9ebf2;
          }

          &:disabled {
            cursor: not-allowed;
          }
        }
      }
    }

    .product-info {
      display: flex;
      flex-direction: column;
      line-height: 1.5;
      font-size: 14px;
      font-weight: 500;

      .product-info-secondary {
        display: block;
        font-size: 12px;
      }

      span {
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }
}

:deep() .products-table {
  tbody {
    .stock-cell {
      overflow: visible;
    }
  }
}

:deep() .table {
  .sku-cell,
  .quantity-cell {
    width: 10rem;
  }
  .category-badge {
    @extend %category-badge;
    max-width: 100%;
  }

  .category-cell {
    width: 16rem;
  }

  .product-cell {
    width: 470px;
  }

  .distributor-cell {
    width: 14rem;

    .distributor-info {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
.popover {
  position: absolute;
  background-color: white;
  margin-top: 10px;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 16px;
  max-width: 360px;
  box-shadow: 0px 2px 4px 2px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  overflow: visible;
}

.quantity {
  @extend %flex-center;
  justify-content: flex-end;

  &__icon {
    padding-left: px-to-rem(24px);
    color: rgba($color-gray-6C, 0.4);
    @include font-size(20px);
  }

  .ez-number-input {
    height: auto;
    width: 90px;
  }

  .add-to-cart-button {
    padding: unset;
    height: auto;
    color: inherit;
    font-size: inherit;

    &--active {
      color: $color-primary-blue;
    }

    &:not(:disabled):hover {
      svg {
        color: #000;
      }
    }

    &:disabled {
      background-color: inherit;
      color: rgba($color-gray-6C, 0.4);
    }
  }
}

.table thead th.add-to-cart-cell {
  padding-right: 46px;
}

.add-to-cart-cell {
  width: 160px;
  :deep() .mask-input-wrapper .mask-input .mask-input__input {
    padding-left: 2px;
  }
}

:deep() .table tbody tr td:last-child {
  overflow: visible;
}

.category-badge {
  @extend %category-badge;
}

.product-info {
  display: flex;
  flex-direction: column;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-info-secondary {
    &--category {
      font-weight: 600;
    }
  }
}

.product-info.responsive-info {
  .product-info-secondary {
    display: none;
  }

  @media (max-width: 1420px) {
    .product-info-secondary {
      display: block;
    }

    :deep() .table {
      .sku-cell,
      .category-cell {
        display: none;
      }
    }
  }

  @media (max-width: 1023px) {
    .product-info-secondary {
      display: none;
    }
  }
}

:deep() .tags {
  display: flex;

  .v-tag {
    min-width: 0;
    flex-shrink: 1;
    overflow: hidden;
  }
}

:deep() .mask-input.mark-input--inline {
  width: 100%;
  .mask-input__input {
    font-weight: normal;
    color: $color-gray-25;
    text-align: center;
  }
}

@media (max-width: 1023px) {
  .product-list.product-list--b2b {
    :deep() table.table.products-table {
      display: none;
    }

    .products-table-mobile--b2b {
      display: block;
    }
  }
}

.products-grid {
  list-style: none;
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(218px, 1fr));
  padding: 0;

  .product-card {
    border: 1px solid #eceef5;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.12);

    &--see-all {
      justify-content: center;
      border: none;
      box-shadow: none;
      font-weight: 600;
      font-size: 14px;
      div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background-color: #f5f6fa;
      }
    }

    hr {
      margin: 12px 0;
    }
    .product-card-image {
      position: relative;
      width: 100%;
      max-width: 100%;
      height: 218px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      img {
        max-width: 100%;
        max-height: 100%;
      }
      .unit-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        text-transform: uppercase;
        padding: 6px;
        background-color: #daf1e9;
        color: #1ba975;
        font-size: 11px;
        font-weight: 700;
        border-radius: 2px;
      }
    }
    .product-card-body {
      padding: 20px;
      text-align: center;
      line-height: 1.5;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      width: 100%;
      align-items: center;

      .product-name {
        margin: 0;
        font-weight: 600;
        font-size: 14px;
        max-height: 70px;
        max-width: 100%;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
      }

      .product-category {
        font-size: 12px;
        color: $color-gray-6C;
        letter-spacing: 0;
      }

      :deep() .v-price--row {
        justify-content: center;
      }

      .trade-deal {
        font-weight: 500;

        &--green {
          color: $color-primary-green;
        }
      }
    }

    .product-card-cart {
      display: flex;
      align-items: center;
      width: 120px;
      margin-bottom: 12px;

      :deep() .mask-input-wrapper {
        margin: 0 8px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }

      button.increment {
        display: flex;
        align-items: center;
        justify-content: center;
        @extend %button-reset;
        flex-shrink: 0;
        @include size(24px);
        background-color: $input-button-bg-color;
        border-radius: 50%;
        color: $color-gray-6C;

        &:hover {
          cursor: pointer;
          background-color: #e9ebf2;
        }

        &:disabled {
          cursor: not-allowed;
        }
      }
    }

    .quantity {
      margin-bottom: 20px;

      .quantity__icon {
        padding-left: 0;
      }
    }
  }
}

.previously-ordered-title {
  display: flex;
  align-items: center;
  justify-content: space-between;

  :deep() .button span {
    display: flex;
    align-items: center;
  }
}

.see-more-link {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  :deep() span {
    display: flex;
    align-items: center;
  }
}
</style>
